import { useCallback, useEffect, useState, useRef } from 'react';
import { isAbortError } from './isAbortError';

type PollOptions<T> = { 
  /* Function that fetches and returns data. * Must throw on error. */
  fetcher: (signal?: AbortSignal) => Promise<T>; /* Normal interval in ms when tab is visible. */
  intervalMs: number; /* Longer interval in ms when tab is hidden. * Defaults to 3x intervalMs if not provided. */
  hiddenDelayMs?: number; /* Optional stop condition: return true to stop polling. */
  shouldStop?: (data: T) => boolean;
};

export function usePolling<T>({
  fetcher, intervalMs, hiddenDelayMs, shouldStop
}: PollOptions<T>) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [isRunning, setIsRunning] = useState(true);
  const abortRef = useRef<AbortController | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const runningRef = useRef(true);
  const runIdRef = useRef(0);
  const clearNext = () => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };
  
  const cancelInFlight = () => {
    abortRef.current?.abort();
    abortRef.current = null;
  };
  const nextDelay = useCallback(() => {
    const hiddenMs = hiddenDelayMs ?? intervalMs * 3;
    return document.visibilityState === "visible" ? intervalMs : hiddenMs;
  }, [hiddenDelayMs, intervalMs]);
  
  const loop = useCallback(async (runId: number) => {
    if (!runningRef.current || runId !== runIdRef.current) return;
    // Cancel any in-flight request before starting a new one
    cancelInFlight();
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      const result = await fetcher(ac.signal);
      if (!runningRef.current || runId !== runIdRef.current) {
        return;
      }
      setData(result);
      setError(null);
      if (shouldStop?.(result)) {
        runningRef.current = false;
        setIsRunning(false); return; 
        // stop scheduling
      }
    } catch (err) { 
      // Basic error handling; you could add backoff here
      if (!runningRef.current || runId !== runIdRef.current) {
        return;
      }
      if (isAbortError(err, ac.signal)) {
        return;
      }
      setError(err);
    } finally {
      // Schedule next tick only after this one completes, with visibility-aware delay
      if (runningRef.current && runId === runIdRef.current) {
        timeoutRef.current = window.setTimeout(() => {
          void loop(runId);
        }, nextDelay());
      }
    }
  }, [fetcher, shouldStop, nextDelay]);
  
  useEffect(() => {
    runningRef.current = true;
    setIsRunning(true);
    const runId = runIdRef.current + 1;
    runIdRef.current = runId;
    void loop(runId);
    const onVisChange = () => {
      if (!runningRef.current || runId !== runIdRef.current) return;
      clearNext();
      if (document.visibilityState === "visible") {
        // Immediately refresh when the tab is focused again
        void loop(runId);
      } else {
        // Re-schedule with hidden delay profile
        timeoutRef.current = window.setTimeout(() => {
          void loop(runId);
        }, nextDelay());
      }
    };
    
    document.addEventListener("visibilitychange", onVisChange);
    return () => {
      if (runId === runIdRef.current) {
        runIdRef.current += 1;
      }
      runningRef.current = false;
      clearNext();
      cancelInFlight();
      document.removeEventListener("visibilitychange", onVisChange);
    };
  }, [loop, nextDelay]);
  
  const stop = useCallback(() => {
    runIdRef.current += 1;
    runningRef.current = false;
    setIsRunning(false);
    clearNext();
    cancelInFlight();
  }, []);
  
  return { data, error, isRunning, stop };
}
